import { StateMachine } from "@/core/arch/state_machine";
import { isTerminal, VisibilityPoller } from "./job_polling";
import type {
  JobHistoryRepository,
  JobHistoryRow,
} from "./job_history_repository";

/**
 * Folds a freshly polled first page into the rows already on screen.
 *
 * Known ids are replaced in place; ids the poll has not seen before are
 * prepended, since the index is time-ordered descending and anything new is
 * newer than everything loaded. Overwriting the leading N rows instead would
 * corrupt the page boundary the moment a run enqueues a successor, because
 * every row below it shifts down by one.
 */
export function mergeFirstPage(
  existing: JobHistoryRow[],
  page: JobHistoryRow[],
): JobHistoryRow[] {
  const polled = new Map(page.map((row) => [row.id, row]));
  const merged = existing.map((row) => polled.get(row.id) ?? row);
  const known = new Set(existing.map((row) => row.id));
  const fresh = page.filter((row) => !known.has(row.id));
  return fresh.length > 0 ? [...fresh, ...merged] : merged;
}

export interface JobHistoryState {
  rows: JobHistoryRow[];
  status: "idle" | "loading" | "loaded" | "error";
  /** Set while a "load more" fetch is in flight, so the first page stays visible. */
  loadingMore: boolean;
  nextToken: string | null;
  error?: string;
}

/**
 * Paginates one job type's run history, newest first.
 *
 * Pages are appended rather than replaced: the index is time-ordered, so an
 * append is the whole of "load more" and there is no client-side sort to redo.
 */
export class JobHistoryBloc extends StateMachine<JobHistoryState> {
  private readonly poller = new VisibilityPoller(() => {
    void this.refreshFirstPage();
  });
  private refreshing = false;
  private disposed = false;

  constructor(
    private readonly repo: JobHistoryRepository,
    private jobType: string,
  ) {
    super({ rows: [], status: "idle", loadingMore: false, nextToken: null });
    this.appendDisposeCallback(this.teardown);
  }

  private readonly teardown = (): void => {
    this.disposed = true;
    this.poller.stop();
  };

  /**
   * Returns a disposed bloc to service.
   *
   * `useStateMachine` disposes on effect cleanup but keeps the same instance in
   * its ref, so React StrictMode's mount/cleanup/mount replay hands the view a
   * bloc that has already been torn down. Re-registering matters as much as
   * clearing the flag: `dispose` consumes its callbacks, so without this a
   * later real unmount would no longer stop the poller.
   */
  private rearm(): void {
    this.disposed = false;
    this.appendDisposeCallback(this.teardown);
  }

  /**
   * Loads the first page. Pass a job type to retarget the bloc — the route
   * parameter can change under a mounted view, and `loadMore` must page the
   * type that is on screen.
   */
  async load(jobType: string = this.jobType): Promise<void> {
    if (this.disposed) this.rearm();
    this.jobType = jobType;
    this.setState({
      rows: [],
      status: "loading",
      loadingMore: false,
      nextToken: null,
    });
    try {
      const page = await this.repo.listHistory(this.jobType);
      this.setState({
        rows: page.rows,
        status: "loaded",
        loadingMore: false,
        nextToken: page.nextToken,
      });
    } catch (err) {
      this.setState({
        rows: [],
        status: "error",
        loadingMore: false,
        nextToken: null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    this.syncPolling();
  }

  async loadMore(): Promise<void> {
    const { nextToken, loadingMore } = this.state;
    if (!nextToken || loadingMore) return;
    this.setState({ ...this.state, loadingMore: true });
    try {
      const page = await this.repo.listHistory(this.jobType, { nextToken });
      // Read the rows again rather than reusing the pre-await copy: a poll that
      // landed during the fetch may have prepended a successor and advanced the
      // running rows, and appending onto the stale array would discard both.
      const current = this.state.rows;
      // A row prepended by the poll shifts the cursor's page boundary, so the
      // next page can repeat a row that is already on screen.
      const loaded = new Set(current.map((row) => row.id));
      this.setState({
        rows: [...current, ...page.rows.filter((row) => !loaded.has(row.id))],
        status: "loaded",
        loadingMore: false,
        nextToken: page.nextToken,
      });
    } catch (err) {
      // Rows and cursor are kept so the loaded pages stay on screen and the
      // failed page can be retried.
      this.setState({
        ...this.state,
        status: "error",
        loadingMore: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    this.syncPolling();
  }

  /**
   * Re-reads the newest page and merges it in, so running rows advance and
   * successor jobs appear without a manual reload.
   *
   * A failed tick is swallowed: the loaded rows are still the best available
   * view, and the next tick retries. Surfacing it would replace a working table
   * with an error banner over a transient blip.
   */
  private async refreshFirstPage(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;
    const jobType = this.jobType;
    try {
      const page = await this.repo.listHistory(jobType);
      // Discard a tick that resolves after the view retargeted or reloaded.
      if (jobType !== this.jobType || this.state.status === "loading") return;
      this.setState({
        ...this.state,
        rows: mergeFirstPage(this.state.rows, page.rows),
      });
    } catch {
      // Intentionally ignored; see above.
    } finally {
      this.refreshing = false;
      this.syncPolling();
    }
  }

  /**
   * Polls only while a loaded row can still change, so an idle page is silent.
   *
   * Gated on disposal because every caller runs after an await: a fetch that
   * resolves once the view has gone would otherwise start a poller that can
   * never be stopped, the dispose callbacks having already run.
   */
  private syncPolling(): void {
    if (this.disposed) return;
    if (this.state.rows.some((row) => !isTerminal(row.status))) {
      this.poller.start();
    } else {
      this.poller.stop();
    }
  }
}
